import {matchPath} from 'react-router-dom';

import {hasValue} from 'web-app/util/typescript';

import RouteConfig, {
    type Crew,
    type RouteConfigPath,
    type Route as RouteConfigRoute,
    type RouteConfigType,
} from './route-config';

type Path = string;
type PathWithCrew = [Path, Crew | undefined];
/**
 * Returns a flattened array of all paths based on RouteConfig
 */
export const flattenRouteConfig = (routeConfig: RouteConfigType): PathWithCrew[] => {
    return Object.values(routeConfig).flatMap(route => flattenRoute(route));
};

/**
 * Recursive function to crawl through nested RouteConfigRoutes
 */
const flattenRoute = (route: RouteConfigRoute, prepend: string = ''): PathWithCrew[] => {
    if (route.emberIdent === 'index') {
        return [];
    }

    const path = `${prepend}${route.path}`;
    const pathWithCrew = [path, route.crew] satisfies PathWithCrew;
    if (!hasValue(route.subRoutes)) {
        return [pathWithCrew];
    }

    return [
        pathWithCrew,
        ...Object.values(route.subRoutes).flatMap(route => {
            return flattenRoute(route, path);
        }),
    ];
};

// Compute flattened route config once
const flattenedRouteConfigWithCrews = flattenRouteConfig(RouteConfig);
const flattenedRouteConfig = flattenedRouteConfigWithCrews.map(([path]) => path);

export const findCrewFromPath = (inputPath: string): Crew | undefined => {
    return flattenedRouteConfigWithCrews.find(([path]) => path === inputPath)?.[1];
};

export const validateStringAsRouteConfigPath = (
    stringPath: string,
    defaultPath: RouteConfigPath = '/login',
): RouteConfigPath => (flattenedRouteConfig.includes(stringPath) ? (stringPath as RouteConfigPath) : defaultPath);

/**
 * @private use findPathFromUrl instead. This is export for test purposes
 */
export const testableFindPathFromUrl = (url: string, flattenedConfig: Path[]): Path | undefined => {
    return flattenedConfig.find(path =>
        hasValue(
            matchPath(url, {
                path,
                exact: true,
                strict: false,
            }),
        ),
    );
};

/**
 *
 * @param url following the structure "/account/institution/cool-inst-id/some/route"
 * @param flattenedConfig should not be used, implemented to support stubbing for tests
 * @returns the matching react-router path - e.g. "/account/institution/:institutionId/some/route"
 */
export const findPathFromUrl = (url: string) => testableFindPathFromUrl(url, flattenedRouteConfig);

/**
 * @deprecated Use `withPath` instead
 */
export const makeBuildSubroutePath =
    (parentPath: string) =>
    /**
     * @deprecated Use `withPath` instead
     */
    (routePath: string | undefined = '') => {
        return `${parentPath}${routePath}`;
    };

/**
 * @private use findRouteTemplateFromUncleanUrl instead. This is export for test purposes
 */
export const testableFindRouteTemplateFromUncleanUrl = (findPath: typeof findPathFromUrl) => (uncleanUrl: string) => {
    const urlWithoutHash = uncleanUrl.split('#')[1];
    const urlWithoutQueryParams = urlWithoutHash?.split('?')[0];
    const routeTemplate = urlWithoutQueryParams ? findPath(urlWithoutQueryParams) : undefined;

    return routeTemplate;
};

/**
 * Converts an "unclean" url to the matching route template.
 *
 * @param uncleanUrl The unclean url. E.g. `#/account/feed/68479d1d-f8c9-4cd3-aa31-ea0c08330315?foo=bar
 *
 * @returns The matching route template. E.g. `/account/feed/:institutionId/
 */
export const findRouteTemplateFromUncleanUrl = testableFindRouteTemplateFromUncleanUrl(findPathFromUrl);
